import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import SimpleDataGrid from './SimpleDataGrid';

// Color azul usado en todo el proyecto (Style.Dialog / Style.InputFieldLabel / Style.SaveButton
// en Styles.jsx no lo exponen suelto, solo mezclado con otras propiedades) -- se repite aca
// literal para el borde de la ventana flotante y el color de la lupa, igual que ya se hace
// en varios lugares de Styles.jsx.
const PROJECT_BLUE = '#191268';

// Selector de Cuenta con busqueda -- reemplaza el <select> plano de "Cuenta" en
// AccountingExpenseCreate.jsx por una lupa que abre una ventana flotante con todas las
// cuentas disponibles, donde se puede filtrar escribiendo el codigo o el nombre de la
// cuenta, ordenar por columna, y elegir haciendo click en la fila.
//
// "accounts" se recibe en el MISMO formato {key, value} que ya usaba el <select> de
// AccountingExpenseCreate.jsx (value = "codigo - nombre", ya filtrado por Centro de Costo
// del lado del padre) -- no dispara ningun llamado de red nuevo, solo re-presenta datos
// que esa pantalla ya tenia cargados en memoria. El primer item ({key:'', value:'Seleccione'})
// que ese array trae para el <select> se descarta aca porque no tiene sentido como fila
// elegible en una tabla.
const AccountPicker = ({ accounts = [], onSelect, disabled }) => {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');

    const rows = useMemo(() => {
        return accounts
            .filter((item) => item.key !== '')
            .map((item) => {
                const code = item.key;
                // "value" viene armado como `${codigo} - ${nombre}` (ver AccountingExpenseCreate.jsx);
                // se separa aca solo para mostrar Codigo/Nombre en columnas propias -- si por algun
                // motivo no viene con ese prefijo, se muestra completo en la columna Nombre.
                const prefix = `${code} - `;
                const name = item.value.startsWith(prefix) ? item.value.slice(prefix.length) : item.value;
                return { key: code, Codigo: code, Nombre: name };
            })
            .filter((row) => {
                const term = filter.trim().toLowerCase();
                if (!term) return true;
                return row.Codigo.toLowerCase().includes(term) || row.Nombre.toLowerCase().includes(term);
            });
    }, [accounts, filter]);

    const handleOpen = () => {
        setFilter('');
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleRowClick = (index, row) => {
        onSelect(row.key);
        setOpen(false);
    };

    // Nombre de cuenta con recorte + tooltip: hay nombres de cuenta demasiado largos para
    // entrar en la columna sin desarmar la tabla -- se recortan visualmente ("..." via
    // textOverflow) y se muestra el nombre completo al pasar el mouse.
    const columns = [
        { key: 'Codigo', name: 'Codigo' },
        {
            key: 'Nombre',
            name: 'Nombre',
            render: (value) => (
                <Tooltip title={value} arrow>
                    <span style={{
                        display: 'inline-block',
                        maxWidth: '20em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'bottom',
                    }}>
                        {value}
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <>
            {/* marginLeft negativo: el input (col-9) y este boton (col-3) son columnas de
                bootstrap contiguas, y el padding propio de esas columnas deja un espacio grande
                entre el cuadro de texto y la lupa -- se acerca "pisando" ese padding en vez de
                tocar InputFieldCustom.jsx (que tambien arma los buscadores de Cliente/Inmueble
                en DownPaymentsGeneral/DownPaymentCreate.jsx y no se quiere afectarlos). */}
            <div style={{ marginLeft: '-1.7em', display: 'inline-block' }}>
                <IconButton onClick={handleOpen} disabled={disabled} style={{ color: PROJECT_BLUE, padding: '4px' }} aria-label="Buscar cuenta" size="small">
                    <SearchIcon />
                </IconButton>
            </div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: '12px' } }}
            >
                {/* El borde va en este div interno, con margen respecto del Paper del Dialog
                    (que queda sin borde propio), para que se vea "despegado" del borde de la
                    ventana en vez de pegado al canto -- en el azul del proyecto. */}
                <div style={{ margin: '10px', border: `2px solid ${PROJECT_BLUE}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <DialogTitle><h3 style={{ fontFamily: 'Gothic', color: PROJECT_BLUE }}>Cuentas</h3></DialogTitle>
                    <DialogContent>
                        <div class="row" style={{ paddingBottom: '1em' }}>
                            <div class="col-12">
                                {/* El "inputProps" (i minuscula, style directo sobre el <input> nativo)
                                    ya se habia probado y no alcanzo -- esta version de MUI (ver
                                    "@mui/material" en package.json) le pone al <input> sus propias clases
                                    (MuiInputBase-input) con mas peso que un style en linea puesto por esa
                                    via en algunos casos. Para que gane siempre, sin depender de como esta
                                    version de MUI resuelva esa prop puntual, se fuerza por CSS con
                                    !important contra esa clase estable de MUI (existe igual en v4/v5/v6). */}
                                <style>{`
                                    .account-picker-filter .MuiInputBase-input {
                                        font-family: 'Gothic' !important;
                                    }
                                `}</style>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    className="account-picker-filter"
                                    placeholder="Filtrar por nombre o codigo de cuenta"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    inputProps={{ style: { fontFamily: 'Gothic' } }}
                                />
                            </div>
                        </div>
                        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                            <SimpleDataGrid
                                columns={columns}
                                rows={rows}
                                onRowClick={handleRowClick}
                                sortable
                            />
                        </div>
                    </DialogContent>
                </div>
            </Dialog>
        </>
    );
};

export default AccountPicker;
